import { useState, useMemo } from "react";
import { updateZoneDescriptionApi } from "../../api/zones.api";
import { ZoneRow } from "./ZonesRow";
import "./ZonesManagementForm.scss";

export default function ZonesManagementForm({ webData, refreshData }) {
  const [editValues, setEditValues] = useState({});
  const [isSaving, setIsSaving] = useState(null);

  // 👇 1. Estado para el buscador
  const [searchTerm, setSearchTerm] = useState("");

  const handleInputChange = (zoneId, value) => {
    setEditValues((prev) => ({ ...prev, [zoneId]: value }));
  };

  const handleSave = async (zoneId) => {
    setIsSaving(zoneId);
    const descriptionToSave =
      editValues[zoneId] !== undefined ? editValues[zoneId] : "";

    try {
      await updateZoneDescriptionApi(zoneId, descriptionToSave);
      setEditValues((prev) => {
        const newState = { ...prev };
        delete newState[zoneId];
        return newState;
      });
      refreshData();
      console.log(`Zona ${zoneId} actualizada con éxito`);
    } catch (error) {
      console.error("Error guardando la descripción:", error);
      alert("Hubo un error al guardar la descripción en la base de datos.");
    } finally {
      setIsSaving(null);
    }
  };

  // 👇 2. Lógica del filtro memorizada
  const filteredData = useMemo(() => {
    // Si no hay texto, devolvemos todo tal cual
    if (!searchTerm) return webData;

    const lowercasedTerm = searchTerm.toLowerCase();

    return (
      webData
        .map((categoryGroup) => {
          // Clonamos el grupo para no mutar la prop original
          const filteredCategory = { ...categoryGroup };

          if (filteredCategory.subzones) {
            // CASO A: Filtrar dentro de subzonas (Ej: Costa -> Marbella)
            const filteredSubzones = filteredCategory.subzones
              .map((subzoneGroup) => {
                const matchedZones = subzoneGroup.zones.filter((zone) =>
                  zone.name?.toLowerCase().includes(lowercasedTerm),
                );
                return { ...subzoneGroup, zones: matchedZones };
              })
              // Eliminamos las subzonas que se han quedado vacías
              .filter((subzoneGroup) => subzoneGroup.zones.length > 0);

            filteredCategory.subzones = filteredSubzones;
          } else if (filteredCategory.zones) {
            // CASO B: Filtrar zonas directas (Ej: Patrimonial)
            filteredCategory.zones = filteredCategory.zones.filter((zone) =>
              zone.name?.toLowerCase().includes(lowercasedTerm),
            );
          }

          return filteredCategory;
        })
        // Por último, eliminamos las categorías que se han quedado totalmente vacías
        .filter((categoryGroup) => {
          if (categoryGroup.subzones) {
            return categoryGroup.subzones.length > 0;
          }
          return categoryGroup.zones && categoryGroup.zones.length > 0;
        })
    );
  }, [webData, searchTerm]);

  // Renderizado principal
  return (
    <div className="zones-management-form">
      <div className="zones-management-form__header">
        <div className="zones-management-form__title">
          <h2 style={{ fontSize: "1.5rem", fontWeight: "bold" }}>
            Administrar Descripciones de Zonas
          </h2>
        </div>
        <input
          type="text"
          placeholder="Buscar zona por nombre..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="zones-management-form__search"
        />
      </div>

      {/* 👇 4. Usamos filteredData en lugar de webData */}
      {filteredData.length === 0 ? (
        <div className="zones-management-form__no-results">
          No se encontraron zonas que coincidan con "{searchTerm}".
        </div>
      ) : (
        filteredData.map((categoryGroup, index) => (
          <div key={index} className="zones-management-form__category-card">
            <h2 className="zones-management-form__category-card-title">
              {categoryGroup.category}
            </h2>

            {categoryGroup.subzones ? (
              <div className="zones-management-form__subzones">
                {categoryGroup.subzones.map((subzoneGroup, subIdx) => (
                  <div key={subIdx}>
                    <h3 className="zones-management-form__subzones-title">
                      {subzoneGroup.subzone}
                    </h3>
                    <div className="zones-management-form__list">
                      {subzoneGroup.zones.map((zone) => (
                        <ZoneRow
                          key={zone._id}
                          zone={zone}
                          editValues={editValues}
                          handleInputChange={handleInputChange}
                          handleSave={handleSave}
                          isSaving={isSaving}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="zones-management-form__list zones-management-form__list--standalone">
                {categoryGroup.zones?.map((zone) => (
                  <ZoneRow
                    key={zone._id}
                    zone={zone}
                    editValues={editValues}
                    handleInputChange={handleInputChange}
                    handleSave={handleSave}
                    isSaving={isSaving}
                  />
                ))}
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}
